/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import inputTheme from './Input.scss';
import Input from 'react-toolbox/lib/input';
import includes from 'lodash/includes';
import { Button, ButtonTypes } from 'common/components/core/button';
import { IconWithPopover } from 'common/components/core/icon';
import { neoUIColors } from 'common/enums/ColorEnum';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import { Loader } from 'common/components/core/loader/Loader';
import StringUtils from 'common/utils/StringUtils';
import DragDrop from 'common/components/core/dropzone/DragDrop';
import { Select } from 'common/components/core/select/Select';
import type { UploadFilesValueType } from 'common/components/core/dropzone/DragDrop';
import {
    InputCaretIcon,
    IconTypes
} from 'common/components/core/icon/InputCaretIcon';
import { SizeEnum } from 'common/enums/UIEnums';
import type { SizeEnumType } from 'common/enums/UIEnums';
import coreIds from 'common/tests/core/coreIds.json';
import calculateNodeHeight from './calculateNodeHeight';

type IconProps = {
    popOverContent?: string,
    theme: { [string]: string }
};

const ErrorIcon = ({ popOverContent, theme }: IconProps) => {
    return (
        <IconWithPopover
            className={theme.errorIcon}
            icon="alert-new"
            popOverContent={popOverContent}
            triggerPopoverOn={popOverContent ? PopoverTriggerEnum.Hover : null}
            preferPlace={PopoverPlaceEnum.Above}
            iconColor={neoUIColors.red500.name}
        />
    );
};
type InputPropTypes = {
    theme: Object,
    isInputLoading?: boolean,
    rightIcon?: string | any,
    leftIcon?: string | any,
    error?: string | any,
    errorIconPopoverContent?: string,
    info?: string | any,
    showErrorIcon?: string | any,
    bottomText?: string,
    inputType?: string,
    icon?: string | any,
    disabled?: boolean,
    className?: string | any,
    invert?: boolean,
    highlightColor: string,
    fieldId?: string,
    textareaRows?: string,
    textAreaMaxRows?: number,
    showRequired?: boolean,
    columnInput?: boolean,
    autoWidth?: boolean,
    isInputFocused?: boolean,
    clearableInput?: boolean,
    inputSize: SizeEnumType,
    testId?: string,
    noResize?: boolean,
    onClearInput?: (event: Event) => void,
    onRemoveFile: () => void,
    onFocus?: () => void,
    onChange?: (
        value: string,
        event: Event
    ) => void | ((value: string, fieldId: string, event: Event) => void),
    onEnter?: () => void
};

export const InputTypes = {
    Solid: 'SOLID',
    Floating: 'FLOATING',
    Raised: 'RAISED',
    Minimal: 'MINIMAL',
    NoBorder: 'NOBORDER'
};
const defaults = {
    theme: inputTheme,
    InputComponent: Input
};

const customInputFactory = ({
    Input = defaults.InputComponent,
    options = { theme: defaults.theme }
}) => {
    class CustomInput extends React.PureComponent<InputPropTypes, void> {
        componentDidMount() {
            const { autoSelect } = this.props;
            if (autoSelect && this.inputRef) {
                const inputEl = this.inputRef.getWrappedInstance().refs.input;
                inputEl.select();
                inputEl.scrollLeft = inputEl.scrollWidth;
            }
        }

        componentDidUpdate(prevProps) {
            if (this.props.value !== prevProps.value) {
                this.updateTextAreaHeight();
            }
        }

        handleTextAreaChange = (
            event: SyntheticKeyboardEvent<HTMLInputElement>
        ) => {
            this.updateTextAreaHeight();
            this.handleChange(event.target.value, event);
        };

        handleSelectInputchange = ({ value }) => {
            this.handleChange(value);
        };

        handleChange = (value: string | UploadFilesValueType, e) => {
            const { regexPattern } = this.props;
            if (regexPattern) {
                const regex = new RegExp(regexPattern);
                if (regex.test(value)) {
                    this.onChange(value, e);
                }
            } else {
                this.onChange(value, e);
            }
        };

        handleClear = e => {
            const { onClearInput } = this.props;
            if (onClearInput) {
                onClearInput(e);
                return;
            }
            this.onChange('', e);
        };

        handleFileChange = (files, e) => {
            this.handleChange(files, e);
        };

        onChange = (value, e) => {
            const { onChange, fieldId } = this.props;
            if (fieldId) {
                onChange && onChange(value, fieldId, e);
            } else {
                onChange && onChange(value, e);
            }
        };

        getInputRef = ref => {
            const { setRef } = this.props;
            this.inputRef = ref;
            setRef && setRef(ref);
        };

        updateTextAreaHeight = () => {
            const { textAreaMaxRows } = this.props;
            if (textAreaMaxRows === undefined) {
                return;
            }

            const element = this.inputRef;
            if (element) {
                const height = calculateNodeHeight({
                    element,
                    maxRows: textAreaMaxRows
                });
                element.style.height = `${height}px`;
            }
        };

        isEnterKeyPressed = (event: SyntheticKeyboardEvent<HTMLElement>) => {
            return event.key === 'Enter' && !event.shiftKey;
        };

        handleTextAreaKeyDown = (
            event: SyntheticKeyboardEvent<HTMLElement>
        ) => {
            const { onEnter } = this.props;

            if (onEnter && this.isEnterKeyPressed(event)) {
                onEnter();
                event.preventDefault();
            }
        };

        render() {
            const {
                theme,
                isInputLoading,
                rightIcon,
                leftIcon,
                error,
                info,
                showErrorIcon,
                bottomText,
                inputType,
                icon,
                disabled,
                className,
                invert,
                children,
                highlightColor,
                value,
                regexPattern,
                type,
                label,
                options,
                placeholder,
                required,
                onRemoveFile,
                textareaRows,
                multiple,
                pattern,
                showRequired,
                isInputFocused,
                labelMeta,
                clearableInput,
                columnInput,
                autoWidth,
                inputSize,
                inputLabelSize,
                isPopOverOpen, //eslint-disable-line no-unused-vars
                isFocused, //eslint-disable-line no-unused-vars
                popOverContent, //eslint-disable-line no-unused-vars
                i18n, //eslint-disable-line no-unused-vars
                onClearInput, //eslint-disable-line no-unused-vars
                autoSelect, //eslint-disable-line no-unused-vars
                setRef, //eslint-disable-line no-unused-vars
                onChange, //eslint-disable-line no-unused-vars
                fieldId, //eslint-disable-line no-unused-vars
                useCapture, //eslint-disable-line no-unused-vars
                autoFocus,
                onBlur,
                testId,
                errorIconPopoverContent,
                textAreaMaxRows,
                noResize,
                ...others
            } = this.props;
            const sizeClass = SizeEnum.getSizeClass({ size: inputSize, theme });
            const labelSizeClass = SizeEnum.getSizeClass({
                size: inputLabelSize,
                theme,
                suffix: 'Label'
            });
            const inputTypeClass = includes(InputTypes, inputType)
                ? theme[`${inputType.toLowerCase()}Input`]
                : null;
            let inputValue = value;
            if (regexPattern && StringUtils.isEmpty(value)) {
                // Regex doesn't work in controlled component
                inputValue = '';
            }

            const innerLabel =
                label && labelMeta ? (
                    <div className={theme.labelInner}>
                        <span className={theme.labelText}>{label}</span>
                        &nbsp;
                        <span className={theme.labelMeta}>({labelMeta})</span>
                    </div>
                ) : (
                    label
                );
            return type === 'file' ? (
                <DragDrop
                    ref={this.getInputRef}
                    theme={theme}
                    onRemoveFile={onRemoveFile}
                    value={inputValue}
                    disabled={disabled}
                    required={required}
                    label={innerLabel}
                    placeholder={placeholder}
                    onChange={this.handleChange}
                    autoFocus={autoFocus}
                    onBlur={onBlur}
                    {...others}
                />
            ) : type === 'textarea' ? (
                <div
                    multiline
                    onMouseEnter={this.props.onMouseEnter}
                    onMouseLeave={this.props.onMouseLeave}
                    data-react-toolbox="input"
                    className={classnames(theme.input, {
                        [className]: className,
                        [theme.disabled]: disabled
                    })}
                >
                    <textarea
                        ref={this.getInputRef}
                        rows={textAreaMaxRows === undefined ? textareaRows : 1}
                        required={required}
                        placeholder={placeholder}
                        className={classnames(
                            theme.inputElement,
                            theme.textarea
                        )}
                        style={{
                            resize: noResize ? 'none' : 'auto'
                        }}
                        type={type}
                        value={inputValue}
                        disabled={disabled}
                        onChange={this.handleTextAreaChange}
                        autoFocus={autoFocus}
                        onBlur={onBlur}
                        onKeyUp={() => {
                            this.updateTextAreaHeight();
                        }}
                        onKeyDown={this.handleTextAreaKeyDown}
                    />
                    <div className={theme.resizer} />
                    <div className={theme.label}>
                        {innerLabel}
                        {required ? (
                            <div className={theme.required}>*</div>
                        ) : null}
                    </div>
                    {children}
                </div>
            ) : type === 'select' ? (
                <div data-react-toolbox="input" className={theme.input}>
                    <Select
                        openMenuOnFocus
                        isClearable={false}
                        theme={theme}
                        placeholder={placeholder}
                        value={value}
                        className={classnames(theme.selectInput, {
                            [className]: className ? true : false
                        })}
                        options={options}
                        onChange={this.handleSelectInputchange}
                        autoFocus={autoFocus}
                        onBlur={onBlur}
                    />
                    <div className={theme.label}>
                        {innerLabel}
                        {required ? (
                            <div className={theme.required}>*</div>
                        ) : null}
                    </div>
                </div>
            ) : (
                <Input
                    testId={testId}
                    {...others}
                    autoFocus={autoFocus}
                    onBlur={onBlur}
                    required={required}
                    multiple={multiple}
                    pattern={pattern}
                    type={type}
                    placeholder={placeholder}
                    value={inputValue}
                    disabled={disabled}
                    onChange={this.handleChange}
                    label={innerLabel}
                    ref={this.getInputRef}
                    floating={inputType === InputTypes.Floating ? true : false}
                    theme={theme}
                    icon={icon}
                    className={classnames({
                        [theme.invert]: invert,
                        [theme.isInputLoading]: isInputLoading,
                        [theme.leftIconWrap]: leftIcon,
                        [theme.rightIconWrap]: rightIcon,
                        [theme.clearableInput]: clearableInput,
                        [theme.showErrorIcon]: showErrorIcon,
                        [theme.disabled]: disabled,
                        [inputTypeClass]: inputTypeClass,
                        [className]: className,
                        [theme[`${highlightColor}Border`]]: highlightColor,
                        [theme.showRequired]: showRequired,
                        [theme.columnInput]: columnInput,
                        [theme.autoWidth]: autoWidth,
                        [sizeClass]: !!sizeClass,
                        [labelSizeClass]: !!labelSizeClass,
                        [theme.isInputFocused]:
                            isInputFocused && theme.isInputFocused
                    })}
                >
                    {leftIcon ? (
                        <span className={theme.leftIcon}>{leftIcon}</span>
                    ) : null}
                    {rightIcon || clearableInput ? (
                        <span className={theme.rightIcon}>
                            {clearableInput && !StringUtils.isEmpty(value) ? (
                                <Button
                                    data-test-id={coreIds.clearInput}
                                    onClick={this.handleClear}
                                    theme={theme}
                                    buttonType={ButtonTypes.Icon}
                                    icon="cancel-thin"
                                    className={theme.clearInputBtn}
                                />
                            ) : null}
                            {rightIcon}
                        </span>
                    ) : null}
                    {showErrorIcon || error || info || isInputLoading ? (
                        <div>
                            {showErrorIcon ? (
                                <ErrorIcon
                                    theme={theme}
                                    popOverContent={errorIconPopoverContent}
                                />
                            ) : null}
                            {error ? (
                                <InputCaretIcon
                                    theme={theme}
                                    iconType={IconTypes.Error}
                                    popOverContent={error}
                                    testId={coreIds.error.errorIcon}
                                />
                            ) : null}
                            {info ? (
                                <InputCaretIcon
                                    theme={theme}
                                    iconType={IconTypes.Info}
                                    popOverContent={info}
                                />
                            ) : null}
                            {isInputLoading ? (
                                <Loader
                                    theme={theme}
                                    active={isInputLoading}
                                    size={SizeEnum.Small}
                                />
                            ) : null}
                        </div>
                    ) : null}
                    {bottomText ? (
                        <span tabIndex={-1} className={theme.bottomText}>
                            {bottomText}
                        </span>
                    ) : null}
                    {children}
                </Input>
            );
        }
    }

    CustomInput.defaultProps = {
        icon: null,
        inputType: InputTypes.Solid
    };
    return themr('CustomInput', options.theme, { withRef: true })(CustomInput);
};

const CustomInput = customInputFactory({});

export { customInputFactory, CustomInput as Input };
export default CustomInput;
